import { useEffect, useMemo, useState } from 'react'
import { WContainer, WImage } from '@components/atoms'
import { OpusCodeNode, OpusNode } from '@gql/graphql'
import styles from './Opuses.module.sass'
import { useTrans } from '@hooks/index'

interface Props {
  opuses: Array<OpusNode | null | undefined>
  className?: string
}

interface SurfaceTab {
  id: string
  label: string
}

interface OpusTabGroup {
  tab: SurfaceTab
  opuses: OpusNode[]
}

interface OpusImageGroup {
  groupKey: string
  opuses: OpusNode[]
}

type SurfaceTabId = string | 'all'

const normalizeText = (value?: string | null) => (value || '').toLowerCase().trim()
const toTabId = (value?: string | null) => normalizeText(value).replace(/\s+/g, '-')

const getOpusCodes = (opusCodes?: Array<OpusCodeNode | null> | null) =>
  (opusCodes || []).filter(Boolean) as OpusCodeNode[]

const getCmFormat = (opusCode: OpusCodeNode) => {
  const lato1Cm = opusCode?.formato?.lato1Cm?.trim()
  const lato2Cm = opusCode?.formato?.lato2Cm?.trim()

  if (lato1Cm && lato2Cm) {
    return `${lato1Cm} x ${lato2Cm}`
  }

  return lato1Cm || lato2Cm || '-'
}

const renderInchesSide = (
  latoIntero?: string | null,
  latoNumeratore?: string | null,
  latoDenominatore?: string | null
) => {
  const intero = latoIntero?.trim()
  const numeratore = latoNumeratore?.trim()
  const denominatore = latoDenominatore?.trim()
  const hasFraction = !!numeratore && !!denominatore

  if (!intero && !hasFraction) {
    return '-'
  }

  return (
    <>
      {intero}
      {hasFraction && (
        <>
          {' '}
          <sup>
            {numeratore} / {denominatore}"
          </sup>
        </>
      )}
    </>
  )
}

const renderInchesFormat = (opusCode: OpusCodeNode) => {
  const firstSide = renderInchesSide(
    opusCode?.formato?.lato1Intero,
    opusCode?.formato?.lato1Numeratore,
    opusCode?.formato?.lato1Denominatore
  )
  const secondSide = renderInchesSide(
    opusCode?.formato?.lato2Intero,
    opusCode?.formato?.lato2Numeratore,
    opusCode?.formato?.lato2Denominatore
  )

  if (firstSide === '-' && secondSide === '-') {
    return '-'
  }

  return (
    <>
      {firstSide} x {secondSide}
    </>
  )
}

const getSurfaceLabel = (opusCode: OpusCodeNode) => {
  const tonalita = opusCode?.articolo?.tonalita?.nome?.trim()
  const superficie = opusCode?.articolo?.superficie?.nome?.trim()

  return [tonalita, superficie].filter(Boolean).join(' ') || '-'
}

const getSurfaceName = (opusCode: OpusCodeNode) =>
  opusCode?.articolo?.superficie?.nome?.trim() || ''

const getOpusSurfaceTab = (opus: OpusNode): SurfaceTab | null => {
  const opusSurfaceNames = getOpusCodes(opus?.opusCodesPerArticoli)
    .map((row) => getSurfaceName(row))
    .filter(Boolean)

  const uniqueSurfaceNames = [...new Set(opusSurfaceNames)]

  let label = ''

  if (uniqueSurfaceNames.length === 1) {
    const [singleSurfaceName] = uniqueSurfaceNames
    label = singleSurfaceName
  }

  return {
    id: toTabId(label),
    label,
  }
}

const groupOpusesByImage = (opuses: OpusNode[]) => {
  const grouped = new Map<string, OpusImageGroup>()

  opuses.forEach((opus) => {
    const imageUrl = opus?.imageUrl?.trim()

    if (imageUrl) {
      const key = `image:${imageUrl}`
      const existingGroup = grouped.get(key)

      if (existingGroup) {
        existingGroup.opuses.push(opus)
        return
      }

      grouped.set(key, {
        groupKey: key,
        opuses: [opus],
      })

      return
    }

    // Without imageUrl, keep opus isolated and do not merge by default.
    const key = `no-image:${opus.id}`
    grouped.set(key, {
      groupKey: key,
      opuses: [opus],
    })
  })

  return [...grouped.values()]
}

const OpusSection = ({ opuses }: { opuses: OpusNode[] }) => {
  const t = useTrans()
  const tabGroups = useMemo(() => {
    const groupsMap = new Map<string, OpusTabGroup>()

    opuses.forEach((opus) => {
      const tab = getOpusSurfaceTab(opus)
      if (!tab) {
        return
      }

      const existingGroup = groupsMap.get(tab.id)
      if (existingGroup) {
        existingGroup.opuses.push(opus)
        return
      }

      groupsMap.set(tab.id, {
        tab,
        opuses: [opus],
      })
    })

    return [...groupsMap.values()]
  }, [opuses])

  const surfaceTabs = useMemo(() => tabGroups.map((group) => group.tab), [tabGroups])
  const [activeSurfaceTab, setActiveSurfaceTab] = useState<SurfaceTabId>('all')

  useEffect(() => {
    if (surfaceTabs.length <= 1) {
      setActiveSurfaceTab('all')
      return
    }

    const hasActiveTab = surfaceTabs.some((tab) => tab.id === activeSurfaceTab)
    if (!hasActiveTab) {
      setActiveSurfaceTab(surfaceTabs[0].id)
    }
  }, [activeSurfaceTab, surfaceTabs])

  const selectedOpuses = useMemo(() => {
    if (surfaceTabs.length <= 1 || activeSurfaceTab === 'all') {
      return opuses
    }

    const matchingGroup = tabGroups.find((group) => group.tab.id === activeSurfaceTab)
    return matchingGroup?.opuses || opuses
  }, [activeSurfaceTab, opuses, surfaceTabs.length, tabGroups])

  const activeOpus = useMemo(
    () => selectedOpuses.find((opus) => !!opus.imageUrl) || selectedOpuses[0] || opuses[0],
    [opuses, selectedOpuses]
  )

  const visibleFormatRows = useMemo(
    () => selectedOpuses.flatMap((opus) => getOpusCodes(opus?.opusCodesPerFormati || [])),
    [selectedOpuses]
  )

  const filteredSurfaceRows = useMemo(
    () => selectedOpuses.flatMap((opus) => getOpusCodes(opus?.opusCodesPerArticoli || [])),
    [selectedOpuses]
  )

  if (!activeOpus) {
    return <></>
  }

  return (
    <section className={styles.opusSection}>
      {!!activeOpus?.nome && <h2 className={styles.title}>{activeOpus.nome}</h2>}

      <div className={styles.layout}>
        {!!activeOpus?.imageUrl && (
          <div className={styles.imageBox}>
            <WImage
              className={styles.image}
              src={activeOpus.imageUrl}
              alt={activeOpus.imageAlt || activeOpus.nome || 'Opus image'}
              maxWidth={300}
              maxHeight={350}
            />
          </div>
        )}

        <div className={surfaceTabs.length > 1 ? styles.panelWrap : styles.panelWrap__nopadding}>
          {surfaceTabs.length > 1 && (
            <div
              className={styles.surfaceTabs}
              role="tablist"
              aria-label={t('Filtro superfici opus')}
            >
              {surfaceTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeSurfaceTab === tab.id}
                  className={`${styles.surfaceTab} ${
                    activeSurfaceTab === tab.id ? styles.surfaceTabActive : ''
                  }`}
                  onClick={() => setActiveSurfaceTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div className={surfaceTabs.length > 1 ? styles.panel : styles.panel__nopadding}>
            <div className={styles.tablesGrid}>
              <div className={styles.tableBlock}>
                <h3 className={styles.blockTitle}>{t('FORMATI')}</h3>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('Pz.')}</th>
                      <th>{t('Formato cm')}</th>
                      <th>{t('Formato pollici')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFormatRows.length > 0 ? (
                      visibleFormatRows.map((opusCode) => (
                        <tr key={opusCode.id}>
                          <td className={styles.qtyCell}>{opusCode?.numPezzi ?? '-'}</td>
                          <td>{getCmFormat(opusCode)}</td>
                          <td className={styles.inchesCell}>{renderInchesFormat(opusCode)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className={styles.emptyRow}>
                        <td colSpan={3}>-</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className={styles.tableBlock}>
                <h3 className={styles.blockTitle}>{t('SUPERFICI')}</h3>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('Codice')}</th>
                      <th>{t('Colore')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSurfaceRows.length > 0 ? (
                      filteredSurfaceRows.map((opusCode) => (
                        <tr key={opusCode.id}>
                          <td className={styles.codeCell}>{opusCode?.codiceCatalogo || '-'}</td>
                          <td>{getSurfaceLabel(opusCode)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className={styles.emptyRow}>
                        <td colSpan={2}>-</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const Opuses = (props: Props) => {
  const { opuses = [], className = '' } = props

  const validOpuses = useMemo(
    () => (opuses || []).filter((opus): opus is OpusNode => !!opus),
    [opuses]
  )

  const imageGroups = useMemo(() => groupOpusesByImage(validOpuses), [validOpuses])

  if (validOpuses.length === 0) {
    return <></>
  }

  return (
    <section className={`${styles.root} ${className}`}>
      <WContainer className={styles.container}>
        {imageGroups.map((group) => (
          <OpusSection key={group.groupKey} opuses={group.opuses} />
        ))}
      </WContainer>
    </section>
  )
}

export default Opuses
